/* eslint-disable */

import React, { useMemo } from 'react';
import { Table, TableBody, TableCell, TableContainer, TableFooter, TableHead, TableRow } from '@mui/material';
import CardAccordion from '../../CardAccordion';
// import { Typography } from 'antd';

/**
 * AdvanceAmountHistory component displays advance amount data in a read-only table format.
 * It calculates row totals and grand total without any editing functionality.
 *
 * @component
 * @param {Object} props - Component props
 * @param {Array} props.data - Array of objects with from, to, amount, and remarks properties
 * @returns {JSX.Element} Read-only table displaying advance amount data
 */
const AdvanceAmountHistory = ({ data = [], index }) => {
	// Calculate row totals and grand total
	const { rowTotals, grandTotal } = useMemo(() => {
		const totals = data.map((item) => {
			const fromValue = parseInt(item.from);
			const toValue = parseInt(item.to);
			const amount = parseFloat(String(item.amount).replace(/,/g, ''));

			// Skip invalid values
			if (isNaN(fromValue) || isNaN(toValue) || isNaN(amount) || toValue < fromValue) {
				return 0;
			}

			// Calculate total: (to - from + 1) * amount
			return (toValue - fromValue + 1) * amount;
		});

		// Calculate grand total
		const total = totals.reduce((sum, rowTotal) => sum + rowTotal, 0);

		return { rowTotals: totals, grandTotal: total };
	}, [data]);

	// Format number with thousand separator and 2 decimal places
	const formatNumber = (value) => {
		if (typeof value !== 'number' || isNaN(value)) {
			return '0.00';
		}
		return value.toLocaleString('en-US', {
			minimumFractionDigits: 2,
			maximumFractionDigits: 2,
		});
	};

	return (
		<>
			<CardAccordion
				title={index === 0 ? 'History (On Creation)' : `History ${index} (On Modification)`}
				customHeaderSX={{
					backgroundColor: '#e1f0ff !important',
					p: '10px',
				}}
				typographySX={{ fontSize: '16px !important' }}
				darkTitle={true}
				sx={{ mb: 2 }}
			>
				<TableContainer
					className="advance-amount-table"
					sx={{
						'& table, & th, & td': {
							border: '1px solid #e0e0e0',
							borderCollapse: 'collapse',
						},
						'& th, & td': {
							p: '5px',
						},
					}}
				>
					<Table aria-label="Advance Amount table">
						<TableHead>
							<TableRow>
								<TableCell sx={{ textAlign: 'center' }}>From</TableCell>
								<TableCell sx={{ textAlign: 'center' }}>To</TableCell>
								<TableCell sx={{ textAlign: 'center' }}>Amount</TableCell>
								<TableCell sx={{ textAlign: 'center' }}>Total Amount</TableCell>
								<TableCell sx={{ textAlign: 'center' }}>Remarks</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{data.length > 0 ? (
								data.map((row, index) => {
									return (
										<TableRow key={index}>
											<TableCell style={{ textAlign: 'center' }}>{row.from}</TableCell>
											<TableCell style={{ textAlign: 'center' }}>{row.to}</TableCell>
											<TableCell style={{ textAlign: 'right' }}>{row.amount}</TableCell>
											<TableCell style={{ textAlign: 'right', fontWeight: 'bold' }}>{formatNumber(rowTotals[index])}</TableCell>
											<TableCell>{row.remarks || ''}</TableCell>
										</TableRow>
									);
								})
							) : (
								<TableRow>
									<TableCell colSpan={5} style={{ textAlign: 'center' }}>
										No data available
									</TableCell>
								</TableRow>
							)}
						</TableBody>
						<TableFooter>
							<TableRow>
								<TableCell colSpan={3} align="right" style={{ fontWeight: 'bold' }}>
									Total:
								</TableCell>
								<TableCell style={{ fontWeight: 'bold', textAlign: 'right' }}>{formatNumber(grandTotal)}</TableCell>
								<TableCell></TableCell>
							</TableRow>
						</TableFooter>
					</Table>
				</TableContainer>
			</CardAccordion>
		</>
	);
};

export default AdvanceAmountHistory;
